const SENSITIVE_FIELD_PATTERN = /(?:password|passwd|current-password|new-password|cc-number|cc-csc|cc-cvv|cc-exp|credit.?card|card.?number|cvv|cvc|one-time-code|\botp\b|auth|token|secret|api[_-]?key|bank|routing|iban|swift)/i;
const FORM_CONTROL_SELECTOR = 'input, select, textarea';
const SUBMIT_CONTROL_SELECTOR = 'button[type="submit"], input[type="submit"], button:not([type])';
const FORM_LIKE_CONTAINER_SELECTOR = '[data-ai-log-form], [data-ailog-form], [role="form"], .form-container';

export function normalizeFormUrl(value, baseUrl = globalThis.location?.href) {
    try {
        const url = new URL(value, baseUrl);

        if (! ['http:', 'https:'].includes(url.protocol)) {
            return null;
        }

        url.username = '';
        url.password = '';
        url.search = '';
        url.hash = '';
        url.hostname = url.hostname.toLowerCase();
        url.pathname = url.pathname === '/' ? '/' : url.pathname.replace(/\/+$/, '');

        return url.toString();
    } catch {
        return null;
    }
}

export function formUrlMatches(configuredUrl, currentUrl) {
    return normalizeFormUrl(configuredUrl, currentUrl) === normalizeFormUrl(currentUrl, currentUrl);
}

export class FormPrivacyFilter {
    shouldIgnore(field) {
        if (! field || field.closest?.('[data-ai-log-ignore="true"], [data-ailog-ignore="true"]')) {
            return true;
        }

        const type = String(field.type || field.getAttribute?.('type') || '').toLowerCase();
        const identity = [field.name, field.id, field.autocomplete, field.getAttribute?.('autocomplete')]
            .filter(Boolean).join(' ');

        return ['password', 'hidden', 'submit', 'button', 'reset', 'image'].includes(type) || SENSITIVE_FIELD_PATTERN.test(identity);
    }

    fieldMetadata(field) {
        if (this.shouldIgnore(field)) {
            return null;
        }

        const type = String(field.type || field.tagName || 'unknown').toLowerCase();
        const name = String(field.name || field.id || field.getAttribute?.('data-ai-log-field') || 'unnamed').slice(0, 100);

        return { field_name: name, field_type: type === 'select-one' ? 'select' : type.slice(0, 50) };
    }
}

export class FormTrackingManager {
    constructor(browser, emit, options = {}) {
        this.browser = browser;
        this.emit = emit;
        this.debug = options.debug === true;
        this.createEventId = options.createEventId ?? (() => `e_form${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`);
        this.privacy = new FormPrivacyFilter();
        this.configurations = [];
        this.states = new Map();
        this.activities = new Map();
        this.listenerRemovers = [];
        this.pageViewId = null;
    }

    setConfigurations(configurations) {
        this.configurations = Array.isArray(configurations) ? configurations : [];
    }

    activate(url, pageViewId) {
        this.stop(false);
        this.pageViewId = pageViewId;
        this.activeConfigurations = this.configurations
            .filter((configuration) => formUrlMatches(configuration.url, url))
            .sort((left, right) => Number(Boolean(right.form_selector)) - Number(Boolean(left.form_selector)));

        if (this.activeConfigurations.length === 0) {
            return;
        }

        this.installProgrammaticSubmitTracking();
        this.detectForms();
        const Observer = this.browser.MutationObserver;

        if (typeof Observer === 'function' && this.browser.document?.documentElement) {
            this.mutationObserver = new Observer(() => this.detectForms());
            this.mutationObserver.observe(this.browser.document.documentElement, { childList: true, subtree: true });
        }
    }

    detectForms() {
        for (const configuration of this.activeConfigurations ?? []) {
            let roots = [];

            try {
                roots = configuration.form_selector
                    ? [...(this.browser.document.querySelectorAll?.(configuration.form_selector) ?? [])]
                    : [...(this.browser.document.querySelectorAll?.('form') ?? [])];

                if (! configuration.form_selector && roots.length === 0) {
                    roots = this.detectFormLikeContainers();
                }
            } catch {
                this.log('Invalid form selector ignored.', configuration.form_selector);
                continue;
            }

            for (const [index, root] of roots.entries()) {
                if (this.isTrackableRoot(root)) {
                    this.attach(root, configuration, index);
                }
            }
        }
    }

    detectFormLikeContainers() {
        const roots = new Set();
        const submitControls = this.browser.document.querySelectorAll?.(SUBMIT_CONTROL_SELECTOR) ?? [];

        for (const submitControl of submitControls) {
            const root = submitControl.closest?.(FORM_LIKE_CONTAINER_SELECTOR);

            if (root && this.isTrackableRoot(root)) {
                roots.add(root);
            }
        }

        return [...roots];
    }

    isTrackableRoot(root) {
        if (root?.tagName?.toLowerCase?.() === 'form') {
            return true;
        }

        const fields = root?.querySelectorAll?.(FORM_CONTROL_SELECTOR) ?? [];
        const hasTrackableField = [...fields].some((field) => this.privacy.fieldMetadata(field) !== null);

        return hasTrackableField && Boolean(root?.querySelector?.(SUBMIT_CONTROL_SELECTOR));
    }

    installProgrammaticSubmitTracking() {
        const prototype = this.browser.HTMLFormElement?.prototype;
        const originalSubmit = prototype?.submit;

        if (! prototype || typeof originalSubmit !== 'function') {
            return;
        }

        const manager = this;
        const trackedSubmit = function (...args) {
            const state = manager.states.get(this);

            if (state) {
                manager.onSubmit(state);
            }

            return originalSubmit.apply(this, args);
        };

        try {
            prototype.submit = trackedSubmit;
            this.listenerRemovers.push(() => {
                if (prototype.submit === trackedSubmit) {
                    prototype.submit = originalSubmit;
                }
            });
        } catch {
            this.log('Programmatic form submission tracking is unavailable.');
        }
    }

    attach(form, configuration, index) {
        if (this.states.has(form) || form.closest?.('[data-ai-log-ignore="true"], [data-ailog-ignore="true"]')) {
            return;
        }

        const state = {
            form,
            configuration,
            formId: this.formIdentifier(form, configuration, index),
            activity: this.activityFor(configuration),
            startedAt: null,
            started: false,
            viewed: false,
            submitted: false,
            settled: false,
            outcome: null,
            succeeded: false,
            abandoned: false,
            validationSubmissionErrorSent: false,
            lastField: null,
            dirtyFields: new Set(),
            completedFields: new Set(),
            focusCount: 0,
            changeCount: 0,
            validationErrorCount: 0,
            submitAttempts: 0,
        };
        this.states.set(form, state);
        state.activity.formIds.add(state.formId);

        this.listen(form, 'focusin', (event) => this.onFocus(state, event.target));
        this.listen(form, 'input', (event) => this.onInput(state, event.target));
        this.listen(form, 'change', (event) => this.onChange(state, event.target));
        this.listen(form, 'focusout', (event) => this.onBlur(state, event.target));
        this.listen(form, 'invalid', (event) => this.onInvalid(state, event.target), true);
        this.listen(form, 'submit', () => this.onSubmit(state), true);
        this.listen(form, 'reset', () => this.onReset(state));

        if (form?.tagName?.toLowerCase?.() !== 'form') {
            this.listen(form, 'click', (event) => this.onFormLikeControlClick(state, event.target));
        }

        this.observeView(state);
    }

    onFormLikeControlClick(state, target) {
        const control = target?.closest?.('button, input');

        if (! control || (typeof state.form.contains === 'function' && ! state.form.contains(control))) {
            return;
        }

        const type = String(control.type || control.getAttribute?.('type') || (control.tagName?.toLowerCase?.() === 'button' ? 'submit' : '')).toLowerCase();

        if (type === 'submit') {
            this.onSubmit(state);
        } else if (type === 'reset') {
            this.onReset(state);
        }
    }

    activityFor(configuration) {
        const key = String(configuration.id || `${configuration.url}|${configuration.form_selector || ''}|${configuration.name}`);

        if (! this.activities.has(key)) {
            this.activities.set(key, {
                eventId: this.createEventId(),
                revision: 0,
                changes: [],
                droppedChanges: 0,
                formIds: new Set(),
                lastField: null,
            });
        }

        return this.activities.get(key);
    }

    observeView(state) {
        const Observer = this.browser.IntersectionObserver;

        if (typeof Observer !== 'function') {
            this.view(state);
            return;
        }

        const observer = new Observer((entries) => {
            if (entries.some((entry) => entry.isIntersecting || entry.intersectionRatio > 0)) {
                this.view(state);
                observer.disconnect();
            }
        }, { threshold: 0.01 });
        observer.observe(state.form);
        this.listenerRemovers.push(() => observer.disconnect());
    }

    view(state) {
        if (! state.viewed) {
            state.viewed = true;
            this.send(state, 'form_view');
        }
    }

    start(state) {
        if (! state.started) {
            state.started = true;
            state.startedAt = Date.now();
            this.send(state, 'form_start');
        }
    }

    onFocus(state, field) {
        const metadata = this.privacy.fieldMetadata(field);
        if (! metadata) return;
        this.start(state);
        state.lastField = metadata.field_name;
        state.focusCount += 1;
        this.send(state, 'field_focus', metadata);
    }

    onInput(state, field) {
        const metadata = this.privacy.fieldMetadata(field);
        if (! metadata) return;
        this.start(state);
        state.lastField = metadata.field_name;
        state.dirtyFields.add(field);
        state.validationSubmissionErrorSent = false;
    }

    onChange(state, field) {
        const metadata = this.privacy.fieldMetadata(field);
        if (! metadata) return;
        this.start(state);
        state.lastField = metadata.field_name;
        state.changeCount += 1;
        this.send(state, 'field_change', metadata);

        if (['select', 'checkbox', 'radio'].includes(metadata.field_type)) {
            this.send(state, 'field_select', metadata);
        }

        if (metadata.field_type === 'file') {
            const files = [...(field.files ?? [])];
            this.send(state, 'file_upload', {
                ...metadata,
                file_count: files.length,
                file_type: files.length === 1 ? String(files[0]?.type || 'unknown').slice(0, 100) : 'multiple',
                file_size: files.reduce((total, file) => total + Math.max(0, Number(file?.size) || 0), 0),
            });
        }

        this.completeField(state, field, metadata);
    }

    onBlur(state, field) {
        const metadata = this.privacy.fieldMetadata(field);
        if (! metadata) return;
        if (state.dirtyFields.delete(field)) {
            state.changeCount += 1;
            this.send(state, 'field_change', metadata);
        }
        this.completeField(state, field, metadata);
    }

    completeField(state, field, metadata) {
        if (! state.completedFields.has(field) && this.isComplete(field, metadata.field_type)) {
            state.completedFields.add(field);
            this.send(state, 'field_complete', metadata);
        }
    }

    isComplete(field, type) {
        if (type === 'checkbox' || type === 'radio') return field.checked === true;
        if (type === 'file') return Number(field.files?.length || 0) > 0;
        return String(field.value ?? '').trim() !== '';
    }

    onInvalid(state, field) {
        const metadata = this.privacy.fieldMetadata(field);
        if (! metadata) return;
        this.start(state);
        state.validationErrorCount += 1;
        this.send(state, 'validation_error', { ...metadata, error_type: this.validationErrorType(field.validity, metadata.field_type) });

        if (! state.validationSubmissionErrorSent) {
            state.validationSubmissionErrorSent = true;
            this.send(state, 'form_submit_error', { error_type: 'validation_error' });
        }
    }

    validationErrorType(validity = {}, fieldType = '') {
        if (validity.valueMissing) return 'required';
        if (validity.tooShort) return 'too_short';
        if (validity.tooLong) return 'too_long';
        if (validity.patternMismatch) return 'pattern_mismatch';
        if (validity.typeMismatch) return fieldType === 'email' ? 'invalid_email' : 'invalid_format';
        if (validity.customError) return 'custom_validation_error';
        return 'invalid_format';
    }

    onSubmit(state) {
        this.start(state);
        state.submitted = true;
        state.settled = false;
        state.outcome = null;
        state.submitAttempts += 1;
        state.validationSubmissionErrorSent = false;
        this.send(state, 'form_submit', this.progress(state), { flush: true, preferBeacon: true });
    }

    notifyNetwork(request, statusCode, errorType = null) {
        for (const state of this.states.values()) {
            if (! state.submitted || state.settled || ! this.requestMatches(state, request)) continue;
            if (errorType || ! statusCode || statusCode >= 400) {
                this.submissionError(state, errorType === 'network_error' || ! statusCode ? 'network_error' : 'server_error');
            } else if (statusCode >= 200 && statusCode < 400) {
                this.submissionSuccess(state);
            }
        }
    }

    requestMatches(state, request) {
        const action = state.form.action || state.form.getAttribute?.('action') || this.browser.location.href;
        return normalizeFormUrl(action, this.browser.location.href) === normalizeFormUrl(request?.url, this.browser.location.href)
            && String(request?.method || 'GET').toUpperCase() !== 'GET';
    }

    submissionSuccess(target) {
        const state = this.resolveState(target);
        if (! state || state.succeeded) return;
        state.succeeded = true;
        state.settled = true;
        state.outcome = 'success';
        this.send(state, 'form_submit_success', this.progress(state));
    }

    submissionError(target, errorType = 'unknown_error') {
        const state = this.resolveState(target);
        if (! state || state.settled) return;
        state.settled = true;
        state.outcome = 'error';
        this.send(state, 'form_submit_error', { error_type: errorType });
    }

    resolveState(target) {
        if (this.states.has(target)) return this.states.get(target);
        if (typeof target === 'string') {
            for (const state of this.states.values()) if (state.formId === target || state.form.matches?.(target)) return state;
        }
        return null;
    }

    abandonAll() {
        for (const state of this.states.values()) {
            if (state.started && ! state.succeeded && ! state.abandoned && (! state.submitted || state.outcome === 'error')) {
                state.abandoned = true;
                this.send(state, 'form_abandon', { ...this.progress(state), last_field: state.lastField }, { flush: false });
            }
        }
    }

    progress(state) {
        return {
            fields_completed: state.completedFields.size,
            time_spent_seconds: state.startedAt ? Math.max(0, Math.floor((Date.now() - state.startedAt) / 1000)) : 0,
        };
    }

    onReset(state) {
        state.dirtyFields.clear();
        state.completedFields.clear();
        state.lastField = null;
        state.submitted = false;
        state.settled = false;
        state.outcome = null;
        this.send(state, 'form_reset');
    }

    send(state, event, metadata = {}, options = {}) {
        const activity = state.activity;
        activity.revision += 1;
        const change = {
            event,
            timestamp: Date.now(),
            form_id: state.formId,
            ...metadata,
        };

        if (activity.changes.length < 200) {
            activity.changes.push(change);
        } else {
            activity.droppedChanges += 1;
        }

        activity.lastField = metadata.last_field || metadata.field_name || activity.lastField;
        const activityStates = [...this.states.values()].filter((candidate) => candidate.activity === activity);
        const startedTimes = activityStates.map((candidate) => candidate.startedAt).filter(Number.isFinite);
        const outcome = activityStates.some((candidate) => candidate.succeeded)
            ? 'success'
            : activityStates.some((candidate) => candidate.submitted && ! candidate.settled)
                ? 'submitted'
                : activityStates.some((candidate) => candidate.outcome === 'error')
                    ? 'error'
                    : 'not_submitted';

        this.emit('form_activity', {
            form_tracker_id: String(state.configuration.id || '').slice(0, 32),
            form_id: `tracker_${String(state.configuration.id || 'configured_page')}`.slice(0, 100),
            form_name: state.configuration.name,
            form_type: state.configuration.form_type,
            page_url: String(normalizeFormUrl(this.browser.location.href, this.browser.location.href) || '').slice(0, 500),
            page_path: String(this.browser.location.pathname || new URL(this.browser.location.href).pathname).slice(0, 500),
            forms_detected: activity.formIds.size,
            form_views: activityStates.filter((candidate) => candidate.viewed).length,
            viewed: activityStates.some((candidate) => candidate.viewed),
            started: activityStates.some((candidate) => candidate.started),
            fields_completed: activityStates.reduce((total, candidate) => total + candidate.completedFields.size, 0),
            focus_count: activityStates.reduce((total, candidate) => total + candidate.focusCount, 0),
            change_count: activityStates.reduce((total, candidate) => total + candidate.changeCount, 0),
            validation_error_count: activityStates.reduce((total, candidate) => total + candidate.validationErrorCount, 0),
            submit_attempts: activityStates.reduce((total, candidate) => total + candidate.submitAttempts, 0),
            submission_status: outcome,
            abandoned: activityStates.some((candidate) => candidate.abandoned),
            last_field: activity.lastField,
            time_spent_seconds: startedTimes.length > 0 ? Math.max(0, Math.floor((Date.now() - Math.min(...startedTimes)) / 1000)) : 0,
            dropped_changes: activity.droppedChanges,
            revision: activity.revision,
            changes: activity.changes.map((entry) => ({ ...entry })),
        }, { ...options, eventId: activity.eventId });
    }

    formIdentifier(form, configuration, index) {
        const configuredSelector = String(configuration.form_selector || '');
        const explicit = (/^#[A-Za-z][\w-]*$/.test(configuredSelector) ? configuredSelector.slice(1) : null)
            || form.id || form.name || form.getAttribute?.('data-ai-log-form-id') || form.getAttribute?.('data-ailog-form-id');
        if (explicit) return String(explicit).replace(/^#/, '').slice(0, 100);
        const source = configuredSelector || `${configuration.url || ''}|${form.action || ''}|${form.className || ''}|${index}`;
        let hash = 0;
        for (let offset = 0; offset < source.length; offset += 1) hash = ((hash << 5) - hash + source.charCodeAt(offset)) | 0;
        return `form_${Math.abs(hash).toString(36)}`;
    }

    listen(target, name, handler, options = false) {
        target.addEventListener(name, handler, options);
        this.listenerRemovers.push(() => target.removeEventListener?.(name, handler, options));
    }

    stop(abandon = true) {
        if (abandon) this.abandonAll();
        this.mutationObserver?.disconnect();
        for (const remove of this.listenerRemovers.splice(0)) remove();
        this.states.clear();
        this.activities.clear();
    }

    log(...values) {
        if (this.debug) this.browser.console?.debug?.('[AI Log forms]', ...values);
    }
}
